/*
 * <pewna-bundle-picker> — "2+2 GRATIS" bundle picker.
 *
 * 4 slots; each slot has 3 cascading selects (product → color → size).
 * Tracks variantId per slot. When all 4 slots have a valid variantId, the
 * submit button enables and POSTs all 4 line items to /cart/add.js, then
 * redirects to /cart, where a Shopify automatic discount ("Buy 2 get 2 free")
 * applies the 50% off.
 *
 * Polish color display: variants are stored in admin with English values
 * (Black/Brown/Beige); we display them in Polish (Czarny/Brąz/Nude) to match
 * the rest of the PEWNA UI.
 */
class PewnaBundlePicker extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-bundle-products]');
    if (!dataEl) return;
    try {
      this.products = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('[pewna-bundle] Invalid JSON', e);
      return;
    }

    this.openBtn       = this.querySelector('[data-bundle-open]');
    this.modal         = this.querySelector('[data-bundle-modal]');
    this.closeBtn      = this.querySelector('[data-bundle-close]');
    this.slotsContainer = this.querySelector('[data-bundle-slots]');
    this.counter       = this.querySelector('[data-bundle-count]');
    this.submitBtn     = this.querySelector('[data-bundle-submit]');
    this.submitLabel   = this.querySelector('[data-bundle-submit-label]');
    this.originalPrice = this.querySelector('[data-bundle-original]');
    this.finalPrice    = this.querySelector('[data-bundle-final]');

    this.slots = Array.from({ length: 4 }, () => ({
      product:   null,
      color:     null,
      variantId: null,
    }));

    this.renderSlots();
    this.recalcPrices();

    this.openBtn?.addEventListener('click', () => this.open());
    this.closeBtn?.addEventListener('click', () => this.close());
    this.modal?.addEventListener('click', (e) => {
      if (e.target === this.modal) this.close();
    });
    this.modal?.addEventListener('cancel', (e) => {
      // Allow Esc to close
    });
    this.submitBtn?.addEventListener('click', () => this.submit());
  }

  open() {
    if (typeof this.modal.showModal === 'function') {
      this.modal.showModal();
    } else {
      this.modal.setAttribute('open', '');
    }
  }

  close() {
    if (typeof this.modal.close === 'function') {
      this.modal.close();
    } else {
      this.modal.removeAttribute('open');
    }
  }

  /* ────────────────────────────────────────────────
   * Slot rendering
   * ──────────────────────────────────────────────── */
  renderSlots() {
    this.slotsContainer.innerHTML = this.slots
      .map((_, i) => this.slotHtml(i))
      .join('');

    this.slotsContainer.querySelectorAll('[data-slot-product]').forEach((sel) => {
      sel.addEventListener('change', (e) =>
        this.onProductChange(+e.target.dataset.slotIndex, e.target.value)
      );
    });
    this.slotsContainer.querySelectorAll('[data-slot-color]').forEach((sel) => {
      sel.addEventListener('change', (e) =>
        this.onColorChange(+e.target.dataset.slotIndex, e.target.value)
      );
    });
    this.slotsContainer.querySelectorAll('[data-slot-size]').forEach((sel) => {
      sel.addEventListener('change', (e) =>
        this.onSizeChange(+e.target.dataset.slotIndex, e.target.value)
      );
    });
  }

  slotHtml(i) {
    const productOpts = this.products
      .map((p) => `<option value="${this.escape(p.handle)}">${this.escape(p.title)}</option>`)
      .join('');
    return `
      <li class="pewna-bundle-slot" data-slot="${i}">
        <span class="pewna-bundle-slot__number">${i + 1}</span>
        <div class="pewna-bundle-slot__selects">
          <select class="pewna-bundle-slot__select" data-slot-product data-slot-index="${i}" aria-label="Produkt ${i + 1}">
            <option value="">Wybierz fason…</option>
            ${productOpts}
          </select>
          <select class="pewna-bundle-slot__select" data-slot-color data-slot-index="${i}" disabled aria-label="Kolor ${i + 1}">
            <option value="">Kolor…</option>
          </select>
          <select class="pewna-bundle-slot__select" data-slot-size data-slot-index="${i}" disabled aria-label="Rozmiar ${i + 1}">
            <option value="">Rozmiar…</option>
          </select>
        </div>
      </li>
    `;
  }

  /* ────────────────────────────────────────────────
   * Cascading selects
   * ──────────────────────────────────────────────── */
  onProductChange(index, handle) {
    const slot = this.slots[index];
    slot.product = handle || null;
    slot.color = null;
    slot.variantId = null;

    const product = this.products.find((p) => p.handle === handle);
    const colorSel = this.qs(index, 'color');
    const sizeSel = this.qs(index, 'size');

    if (!product) {
      colorSel.innerHTML = '<option value="">Kolor…</option>';
      colorSel.disabled = true;
      sizeSel.innerHTML = '<option value="">Rozmiar…</option>';
      sizeSel.disabled = true;
      this.update();
      return;
    }

    const colors = [...new Set(product.variants.map((v) => v.o1))];
    colorSel.innerHTML =
      '<option value="">Kolor…</option>' +
      colors
        .map((c) => `<option value="${this.escape(c)}">${this.escape(this.colorDisplay(c))}</option>`)
        .join('');
    colorSel.disabled = false;

    sizeSel.innerHTML = '<option value="">Rozmiar…</option>';
    sizeSel.disabled = true;

    this.update();
  }

  onColorChange(index, color) {
    const slot = this.slots[index];
    slot.color = color || null;
    slot.variantId = null;

    const product = this.products.find((p) => p.handle === slot.product);
    const sizeSel = this.qs(index, 'size');

    if (!color || !product) {
      sizeSel.innerHTML = '<option value="">Rozmiar…</option>';
      sizeSel.disabled = true;
      this.update();
      return;
    }

    const sizes = product.variants
      .filter((v) => v.o1 === color)
      .map((v) => ({ size: v.o2, id: v.id, available: v.a }));

    sizeSel.innerHTML =
      '<option value="">Rozmiar…</option>' +
      sizes
        .map(
          (s) =>
            `<option value="${s.id}" ${s.available ? '' : 'disabled'}>${this.escape(s.size)}${s.available ? '' : ' — wyprzedane'}</option>`
        )
        .join('');
    sizeSel.disabled = false;

    this.update();
  }

  onSizeChange(index, variantIdStr) {
    const slot = this.slots[index];
    slot.variantId = variantIdStr ? parseInt(variantIdStr, 10) : null;
    this.update();
  }

  qs(slotIndex, kind) {
    return this.slotsContainer.querySelector(`[data-slot="${slotIndex}"] [data-slot-${kind}]`);
  }

  /* ────────────────────────────────────────────────
   * State / counters
   * ──────────────────────────────────────────────── */
  update() {
    const filled = this.slots.filter((s) => s.variantId).length;
    this.counter.textContent = filled;
    this.recalcPrices();

    if (filled === 4) {
      this.submitBtn.disabled = false;
      this.submitLabel.textContent = 'Dodaj zestaw do koszyka';
    } else {
      this.submitBtn.disabled = true;
      const left = 4 - filled;
      this.submitLabel.textContent =
        left === 1
          ? 'Wybierz jeszcze 1 sztukę'
          : left < 5
          ? `Wybierz jeszcze ${left} sztuki`
          : `Wybierz jeszcze ${left} sztuk`;
    }
  }

  recalcPrices() {
    // Compute potential total based on selected variants; if no full
    // selection, fall back to 4× first-product price for display hint.
    const filled = this.slots.filter((s) => s.variantId);
    let unitCents;

    if (filled.length > 0) {
      // Use the first selected variant's product price as a hint (most
      // PEWNA products are the same price so this is accurate enough for
      // the in-modal preview; cart shows the real total).
      const firstHandle = filled[0].product;
      const firstProduct = this.products.find((p) => p.handle === firstHandle);
      unitCents = firstProduct ? firstProduct.price : 14900;
    } else {
      unitCents = this.products[0]?.price || 14900;
    }

    const fullCents = unitCents * 4;
    const discountedCents = unitCents * 2;

    if (this.originalPrice) this.originalPrice.textContent = this.formatMoney(fullCents);
    if (this.finalPrice) this.finalPrice.textContent = this.formatMoney(discountedCents);
  }

  /* ────────────────────────────────────────────────
   * Helpers
   * ──────────────────────────────────────────────── */
  colorDisplay(value) {
    const map = {
      Black: 'Czarny',
      Czarny: 'Czarny',
      Brown: 'Brąz',
      'Brąz': 'Brąz',
      Braz: 'Brąz',
      Chocolate: 'Brąz',
      Beige: 'Nude',
      'Beż': 'Nude',
      Bez: 'Nude',
      Nude: 'Nude',
    };
    return map[value] || value;
  }

  escape(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[c]));
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }
    const value = (cents / 100).toFixed(2).replace('.', ',');
    return `${value} zł`;
  }

  /* ────────────────────────────────────────────────
   * Submission
   * ──────────────────────────────────────────────── */
  async submit() {
    const items = this.slots
      .filter((s) => s.variantId)
      .map((s) => ({ id: s.variantId, quantity: 1 }));

    if (items.length !== 4) return;

    this.submitBtn.disabled = true;
    this.submitLabel.textContent = 'Dodawanie do koszyka…';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items }),
      });

      if (response.ok) {
        window.location.href = '/cart';
        return;
      }

      const err = await response.json().catch(() => ({}));
      window.alert('Błąd: ' + (err.description || err.message || 'spróbuj ponownie'));
      this.submitBtn.disabled = false;
      this.submitLabel.textContent = 'Dodaj zestaw do koszyka';
    } catch (e) {
      console.error('[pewna-bundle] Submit error', e);
      window.alert('Błąd połączenia. Spróbuj ponownie.');
      this.submitBtn.disabled = false;
      this.submitLabel.textContent = 'Dodaj zestaw do koszyka';
    }
  }
}

customElements.define('pewna-bundle-picker', PewnaBundlePicker);
